// apps/spa/src/hooks/useReminders.ts
// TanStack Query wrappers for /api/reminders CRUD.
//
// Surface (locked for 03-03 + Phase 4 reuse):
//   useReminders()          — list view (queryKey ['reminders'])
//   useReminder(id)         — edit form pre-fill (queryKey ['reminders', id])
//   useCreateReminder()     — POST /api/reminders (full body)
//   useUpdateReminder(id)   — PUT /api/reminders/:id (full replace; PATCH lives
//                             in useToggleReminder which only sends { enabled })
//
// All mutations invalidateQueries(['reminders']) onSuccess so the list view
// refetches with the authoritative server state after a write.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Reminder } from '@guardian/shared';
import { api } from '@/lib/api';

export type ReminderWriteBody = {
  name: string;
  channelId: string;
  embedJson: string;
  scheduleCron?: string;
  triggerType: 'cron' | 'event' | 'both';
  enabled: boolean;
  // Phase 4 (Plan 04-02): event-trigger binding fields (all optional for backward compatibility)
  event_id?: string | null;
  event_match_mode?: 'specific' | 'all' | null;
  event_lead_minutes?: number | null;
};

export function useReminders() {
  return useQuery({
    queryKey: ['reminders'] as const,
    queryFn: () => api<Reminder[]>('/api/reminders'),
    // Phase 5 (Plan 05-02): surface cron-driven state changes (auto-disable,
    // lastPostedAt) without requiring a manual page reload.
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

export function useReminder(id: string | undefined) {
  return useQuery({
    queryKey: ['reminders', id] as const,
    queryFn: () => api<Reminder>(`/api/reminders/${id}`),
    enabled: !!id,
  });
}

export function useCreateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReminderWriteBody) =>
      api<Reminder>('/api/reminders', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] });
    },
  });
}

export function useUpdateReminder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReminderWriteBody) =>
      api<Reminder>(`/api/reminders/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] });
      qc.invalidateQueries({ queryKey: ['reminders', id] });
    },
  });
}
